import "primeicons/primeicons.css";
import { useEffect, useState } from "react";
import { ApiEndpoints } from "../../constants/appConstants";
import httpService from "../../services/httpService";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { useNavigate } from "react-router";
import { useAppContext } from "../../Context";

interface TableRow {
  tierId: number;
  tierName: string;
  performanceAreaTitle: string;
  performanceAreaDescription: string;
  standardTitle: string;
  standardDescription: string;
  measureNumber: string;
  measureTitle: string;
  measureDescription: string;
}

const logPerformanceStandardsError = (method: string, error: unknown) => {
  const details: string[] = [];
  let current: unknown = error;
  const visited = new Set<unknown>();

  for (let depth = 0; depth < 5 && current != null && !visited.has(current); depth++) {
    visited.add(current);
    if (current instanceof Error) {
      details.push(`${current.name}: ${current.message}`);
      current = (current as Error & { cause?: unknown }).cause;
    } else if (typeof current === "object") {
      const exception = current as { message?: unknown; cause?: unknown; status?: unknown };
      details.push(typeof exception.message === "string" ? exception.message : `Request failed (status: ${String(exception.status ?? "unknown")})`);
      current = exception.cause;
    } else {
      details.push(String(current));
      break;
    }
  }

  console.error(`[PerformanceStandardsComponent.${method}] ${details.join(" | ") || "Unknown error"}`);
};

export const PerformanceStandardsComponent = (props: any) => {
  const navigate = useNavigate();

  const [data, setData] = useState<TableRow[]>([]);
  const [filteredData, setFilteredData] = useState<TableRow[]>(data);

  const [selectedMeasureStatus, setSelectedMeasureStatus] =
    useState<string | null>(null);

  const [pbcTypeOptions, setPbcTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [measureStatusOptions, setMeasureStatusOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const {
    providerType: contextProviderType,
    reviewYear: contextReviewYear
  } = useAppContext();

  const providerType =
    props.providerType ?? contextProviderType;

  const reviewYear =
    props.reviewYear ?? contextReviewYear;

  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);

    httpService
      .get(`${ApiEndpoints.GetAllPerformanceStandards}/${providerType}`)
      .then((response: any) => {
        const result = response?.data.map((item: any) => ({
          ...item,
          customProviderType: `${item.providerType} : ${item.tierName}`,
          customPerformanceArea:
            `${item.performanceAreaTitle} : ${item.performanceAreaDescription}`
        }));

        setData(result);
        setFilteredData(result);
      })
      .catch((error: any) => {
        if (
          error.status == 403 ||
          error.status == 401
        ) {
          navigate("/unauthorized");
        }

        logPerformanceStandardsError("GetAllPerformanceStandards", error);
      })
      .finally(() => {
        setLoading(false);
      });

    httpService
      .get(ApiEndpoints.GetProviderTypes)
      .then((resp: any) => {
        const pbcOptions =
          resp?.data.map(
            (type: any) => ({
              label:
                type.providerType ||
                type,
              value:
                type.providerCode ||
                type
            })
          );

        setPbcTypeOptions(
          pbcOptions || []
        );
      })
      .catch((error: any) => {
        logPerformanceStandardsError("GetProviderTypes", error);
      });

    httpService
      .get(ApiEndpoints.GetMeasureStatuses)
      .then((resp: any) => {
        const statusOptions =
          resp?.data.map(
            (status: any) => ({
              label:
                status.measureStatus ||
                status,
              value:
                status.measureStatus ||
                status,
              id:
                status.measureStatusId
            })
          );

        setMeasureStatusOptions(
          statusOptions || []
        );
      })
      .catch((error: any) => {
        logPerformanceStandardsError("GetMeasureStatuses", error);
      });
  }, [navigate]);

  useEffect(() => {
    let filtered = data;

    if (providerType) {
      filtered = filtered.filter(
        (item: any) =>
          item.providerType ===
          providerType
      );
    }

    if (selectedMeasureStatus) {
      filtered = filtered.filter(
        (item: any) =>
          item.measureStatus ===
          selectedMeasureStatus
      );
    }

    setFilteredData(filtered);
  }, [
    providerType,
    selectedMeasureStatus,
    data
  ]);

  const performanceAreaTemplate = (
    rowData: any
  ) => {
    return (
      <p>
        {rowData.performanceAreaTitle} :{" "}
        {rowData.performanceAreaDescription}
      </p>
    );
  };

  const providerTypeTemplate = (
    rowData: any
  ) => {
    const providerTypeLabel =
      pbcTypeOptions.find(
        option =>
          option.value
            .toLowerCase() ===
          rowData.providerType
            .toLowerCase()
      )?.label;

    return (
      <p>
        {providerTypeLabel ||
          rowData.providerType}{" "}
        : {rowData.tierName}
      </p>
    );
  };

  const smeNameTemplate = (
    rowData: any
  ) => {
    if (
      !rowData.smeUsers ||
      rowData.smeUsers.length === 0
    ) {
      return <span>No SME</span>;
    }

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px"
        }}
      >
        {rowData.smeUsers.map(
          (
            email: string,
            idx: number
          ) => (
            <span
              key={idx}
              style={{
                background:
                  "#e0e0e0",
                borderRadius:
                  "12px",
                padding:
                  "2px 8px",
                fontSize:
                  "0.9em"
              }}
            >
              {email}
            </span>
          )
        )}
      </div>
    );
  };

  const measureDescriptionTemplate = (
    rowData: any
  ) => {
    const maxLength = 80;

    const description =
      rowData.measureDescription ||
      "";

    const isLong =
      description.length >
      maxLength;

    const shortDesc =
      isLong
        ? description.substring(
            0,
            maxLength
          ) + "..."
        : description;

    return (
      <span
        title={description}
        data-pr-tooltip={
          description
        }
        style={{
          whiteSpace:
            "nowrap",
          overflow:
            "hidden",
          textOverflow:
            "ellipsis",
          display:
            "block",
          maxWidth:
            "250px",
          cursor:
            isLong
              ? "pointer"
              : "default"
        }}
      >
        {shortDesc}
      </span>
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px 0px"
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px"
          }}
        >
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px"
            }}
          >
            Measure Status
          </label>

          <Dropdown
            value={
              selectedMeasureStatus
            }
            onChange={(e) =>
              setSelectedMeasureStatus(
                e.value
              )
            }
            options={
              measureStatusOptions
            }
            optionLabel="label"
            placeholder="Measure Status"
            style={{
              width: "200px"
            }}
            showClear
          />
        </div>
      </div>

      <div
        className="card"
        style={{
          marginTop: 20
        }}
      >
        <div>
          <DataTable
            value={filteredData}
            loading={isLoading}
            paginator
            rows={5}
            rowsPerPageOptions={[
              5,
              10,
              25,
              50
            ]}
            emptyMessage="No Performance Standards Found."
            id="PerformanceStandards"
            selectionMode="single"
            onSelectionChange={(e: any) => {
              const selected = e.value;
              if (!selected?.standardMeasureId) {
                console.error("[PerformanceStandardsComponent] Selected measure is missing standardMeasureId", selected);
                return;
              }
              const selectedProviderType = selected.providerType || providerType || "";
              const providerTypeName = pbcTypeOptions.find(
                option => option.value.toLowerCase() === selectedProviderType.toLowerCase()
              )?.label || selectedProviderType;

              navigate(
                `/measureDetails/${selected.standardMeasureId}?providerType=${encodeURIComponent(selectedProviderType)}`,
                {
                  state: {
                    standardMeasureId: selected.standardMeasureId,
                    providerType: selectedProviderType,
                    providerTypeName,
                    measureStatusOptions,
                    performanceAreaTitle: selected.performanceAreaTitle,
                    reviewYear
                  }
                }
              );
            }}
          >
            <Column
              field="customProviderType"
              header="PBC Type"
              sortable
              filter
              style={{
                minWidth: "8rem"
              }}
              body={
                providerTypeTemplate
              }
            />

            <Column
              field="measureNumber"
              header="Measure Code"
              sortable
              filter
              style={{
                minWidth:
                  "12rem"
              }}
            />

            <Column
              field="measureDescription"
              header="Measure Description"
              sortable
              filter
              style={{
                minWidth:
                  "12rem"
              }}
              body={
                measureDescriptionTemplate
              }
            />

            <Column
              field="customPerformanceArea"
              header="Performance Area"
              body={
                performanceAreaTemplate
              }
              sortable
              filter
              style={{
                minWidth:
                  "18rem"
              }}
            />

            <Column
              field="smeUsers"
              header="SME"
              sortable
              filter
              filterMatchMode="custom"
              filterFunction={(
                value,
                filter
              ) => {
                if (!filter) {
                  return true;
                }

                if (!value) {
                  return false;
                }

                return value.some(
                  (
                    name: string
                  ) =>
                    name
                      .toLowerCase()
                      .includes(
                        filter.toLowerCase()
                      )
                );
              }}
              style={{
                minWidth:
                  "18rem"
              }}
              body={
                smeNameTemplate
              }
            />
          </DataTable>
        </div>
      </div>
    </>
  );
};